<template lang="pug">
.org-filter-row(:class="{ compact: hideLabel }")
  span.org-filter-label(v-if="!hideLabel") {{ label }}
  .org-dropdown(:class="{ 'align-end': align === 'end' }" ref="rootRef")
    button.org-dropdown-trigger(
      type="button"
      :class="{ open: isOpen, selected: !!selectedOrg }"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="toggleOpen"
    )
      template(v-if="selectedOrg")
        img.org-avatar(
          :src="selectedOrg.avatar || defaultAvatar"
          :alt="selectedOrg.name"
          @error="handleAvatarError"
        )
        span.org-trigger-name {{ selectedOrg.name }}
        span.org-count-badge 1
      span.org-placeholder(v-else) Filter by organization
      img.org-chevron(
        :class="{ rotated: isOpen }"
        src="@/assets/icons/chevron-down.svg"
        alt=""
      )
    .org-dropdown-panel(v-if="isOpen" ref="panelRef")
      .org-search
        .org-search-field
          img.org-search-icon(src="@/assets/icons/searchIcon.svg" alt="")
          input(
            ref="searchInputRef"
            v-model="query"
            type="text"
            placeholder="Search organizations…"
            @keydown.esc="close"
          )
          button.org-search-clear(
            v-if="query"
            type="button"
            aria-label="Clear search"
            @click="query = ''"
          )
            img(src="@/assets/icons/close.svg" alt="")
      .org-list(role="listbox")
        .org-empty(v-if="filteredOrgs.length === 0") No organizations found
        button.org-option(
          v-for="org in filteredOrgs"
          :key="org.id"
          type="button"
          role="option"
          :class="{ active: org.id === modelValue }"
          :aria-selected="org.id === modelValue"
          @click="selectOrg(org.id)"
        )
          img.org-avatar(
            :src="org.avatar || defaultAvatar"
            :alt="org.name"
            @error="handleAvatarError"
          )
          span.org-option-name {{ org.name }}
          img.org-check(
            v-if="org.id === modelValue"
            src="@/assets/icons/check.svg"
            alt=""
          )
      .org-footer {{ filteredOrgs.length }} of {{ orgs.length }} organizations
  button.org-clear(
    v-if="selectedOrg"
    type="button"
    @click="clear"
  )
    img(src="@/assets/icons/close.svg" alt="")
    span Clear
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick,
} from 'vue';
import defaultAvatarImg from '@/assets/images/default-profile.jpeg';

export interface ShopOrgOption {
  id: string;
  name: string;
  avatar?: string;
}

export default defineComponent({
  name: 'ShopOrgFilterDropdown',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    orgs: {
      type: Array as () => ShopOrgOption[],
      default: () => [],
    },
    label: {
      type: String,
      default: 'Filter by Org',
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: 'start',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const defaultAvatar = defaultAvatarImg;
    const rootRef = ref<HTMLElement | null>(null);
    const panelRef = ref<HTMLElement | null>(null);
    const searchInputRef = ref<HTMLInputElement | null>(null);
    const isOpen = ref(false);
    const query = ref('');
    const VIEWPORT_PADDING = 16;

    const selectedOrg = computed(() => (
      props.orgs.find((org) => org.id === props.modelValue) || null
    ));

    const filteredOrgs = computed(() => {
      const term = query.value.trim().toLowerCase();
      if (!term) {
        return props.orgs;
      }
      return props.orgs.filter((org) => org.name.toLowerCase().includes(term));
    });

    const resetPanelPosition = () => {
      const panel = panelRef.value;
      if (!panel) return;
      panel.style.transform = '';
    };

    const adjustPanelPosition = () => {
      const panel = panelRef.value;
      if (!panel) return;

      resetPanelPosition();

      const rect = panel.getBoundingClientRect();
      let offsetX = 0;

      if (rect.left < VIEWPORT_PADDING) {
        offsetX = VIEWPORT_PADDING - rect.left;
      } else if (rect.right > window.innerWidth - VIEWPORT_PADDING) {
        offsetX = window.innerWidth - VIEWPORT_PADDING - rect.right;
      }

      if (offsetX !== 0) {
        panel.style.transform = `translateX(${offsetX}px)`;
      }
    };

    const close = () => {
      isOpen.value = false;
      query.value = '';
      resetPanelPosition();
    };

    const toggleOpen = () => {
      isOpen.value = !isOpen.value;
      if (!isOpen.value) {
        query.value = '';
      }
    };

    const selectOrg = (id: string) => {
      emit('update:modelValue', props.modelValue === id ? '' : id);
      close();
    };

    const clear = () => {
      emit('update:modelValue', '');
      close();
    };

    const handleAvatarError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (!target || target.src === defaultAvatar) return;
      target.src = defaultAvatar;
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (!isOpen.value || !rootRef.value) return;
      if (!rootRef.value.contains(event.target as Node)) {
        close();
      }
    };

    watch(isOpen, async (open) => {
      if (!open) return;
      await nextTick();
      adjustPanelPosition();
      searchInputRef.value?.focus();
    });

    const handleWindowResize = () => {
      if (isOpen.value) {
        adjustPanelPosition();
      }
    };

    onMounted(() => {
      document.addEventListener('mousedown', handleDocumentClick);
      window.addEventListener('resize', handleWindowResize);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', handleDocumentClick);
      window.removeEventListener('resize', handleWindowResize);
    });

    return {
      defaultAvatar,
      rootRef,
      panelRef,
      searchInputRef,
      isOpen,
      query,
      selectedOrg,
      filteredOrgs,
      toggleOpen,
      selectOrg,
      clear,
      close,
      handleAvatarError,
    };
  },
});
</script>

<style lang="scss" scoped>
.org-filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0;

  &.compact {
    margin: 0;
  }
}

.org-filter-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #9ca3af;
  white-space: nowrap;
}

.org-dropdown {
  position: relative;

  &.align-end {
    .org-dropdown-panel {
      left: auto;
      right: 0;
    }
  }
}

.org-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  max-width: 280px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #d1d5db;
  }

  &.open {
    box-shadow: 0 0 0 3px rgba(43, 181, 160, 0.12);
  }

  &.selected {
    border-color: #2bb5a0;
    color: #2bb5a0;
  }
}

.org-placeholder {
  flex: 1;
  min-width: 0;
  text-align: left;
  color: #6b7280;
}

.org-trigger-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-count-badge {
  margin-left: 2px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(43, 181, 160, 0.1);
  color: #2bb5a0;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
}

.org-chevron {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  margin-left: auto;
  opacity: 0.45;
  transition: transform 0.15s ease;

  &.rotated {
    transform: rotate(180deg);
  }
}

.org-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
}

.org-dropdown-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  width: 288px;
  background: #ffffff;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.org-search {
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.org-search-field {
  position: relative;

  input {
    width: 100%;
    padding: 8px 32px 8px 32px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;
    outline: none;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      border-color: #2bb5a0;
    }
  }
}

.org-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  width: 13px;
  height: 13px;
  transform: translateY(-50%);
  opacity: 0.7;
  pointer-events: none;
}

.org-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  img {
    width: 12px;
    height: 12px;
    opacity: 0.45;
  }
}

.org-list {
  max-height: 288px;
  overflow-y: auto;
  padding: 6px 0;
}

.org-empty {
  padding: 24px 12px;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.org-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  .org-avatar {
    width: 32px;
    height: 32px;
  }

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: rgba(43, 181, 160, 0.08);

    .org-option-name {
      color: #2bb5a0;
      font-weight: 600;
    }
  }
}

.org-option-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #374151;
}

.org-check {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  filter: invert(54%) sepia(72%) saturate(459%) hue-rotate(127deg) brightness(91%) contrast(89%);
}

.org-footer {
  padding: 8px 12px;
  border-top: 1px solid #f3f4f6;
  font-size: 11px;
  color: #9ca3af;
}

.org-clear {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.15s ease;

  img {
    width: 12px;
    height: 12px;
    opacity: 0.5;
  }

  &:hover {
    color: #4b5563;
  }
}

@media (max-width: 768px) {
  .org-filter-row.compact {
    flex: 1 1 100%;
    width: 100%;
    min-width: 0;
  }

  .org-filter-row.compact .org-dropdown {
    flex: 1;
    width: 100%;
    min-width: 0;
  }

  .org-dropdown.align-end .org-dropdown-panel {
    left: 0;
    right: auto;
  }

  .org-dropdown-trigger {
    width: 100%;
    max-width: 100%;
  }

  .org-dropdown-panel {
    left: 0;
    right: auto;
    width: 100%;
    max-width: 100%;
  }
}
</style>
